import { useParams, Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Clock, ArrowLeft, Paintbrush, ChevronRight } from "lucide-react";
import { guidesData } from "@/data/guidesData";
import Footer from "@/components/Footer";
import ReactMarkdown from "react-markdown";

export default function GuideArticlePage() {
  const { slug } = useParams();
  const guide = guidesData.find((g) => g.slug === slug);

  if (!guide) {
    return <Navigate to="/guider" replace />;
  }

  // Get other guides for "Read more" section (3 articles)
  const otherGuides = guidesData.filter((g) => g.id !== guide.id).slice(0, 3);

  // Custom renderer for internal links in markdown content
  const markdownComponents = {
    a: ({ href, children, ...props }) => {
      if (!href) return <a {...props}>{children}</a>;
      // Hash link to landing page section -> use anchor (hash navigation + scroll)
      if (href.startsWith("/#")) {
        return (
          <a href={href} {...props}>
            {children}
          </a>
        );
      }
      // Internal SPA link -> React Router Link
      if (href.startsWith("/")) {
        return (
          <Link to={href} {...props}>
            {children}
          </Link>
        );
      }
      // External link
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    },
  };

  return (
    <main className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="container mx-auto max-w-6xl px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#b91c1c] rounded-lg flex items-center justify-center">
              <Paintbrush className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold">fasadmalning.se</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/" className="text-slate-300 hover:text-white transition-colors">
              Hem
            </Link>
            <Link to="/guider" className="text-white font-medium">
              Guider
            </Link>
          </nav>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="container mx-auto max-w-4xl px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-slate-600">
            <Link to="/" className="hover:text-slate-900 transition-colors">Hem</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/guider" className="hover:text-slate-900 transition-colors">Guider</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 font-medium truncate">{guide.title}</span>
          </nav>
        </div>
      </div>

      {/* Article */}
      <article className="py-12 md:py-16" data-testid="guide-article">
        <div className="container mx-auto max-w-4xl px-6">
          {/* Header */}
          <header className="mb-10 space-y-6">
            <Link
              to="/guider"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tillbaka till guider
            </Link>

            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#b91c1c] bg-[#b91c1c]/10 px-3 py-1 rounded-full">
                {guide.category}
              </span>
              <span className="flex items-center gap-1 text-sm text-slate-500">
                <Clock className="w-4 h-4" />
                {guide.readTime} lästid
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
              {guide.title}
            </h1>

            <p className="text-xl text-slate-600 leading-relaxed">
              {guide.excerpt}
            </p>
          </header>

          {/* Featured Image */}
          <div className="rounded-2xl overflow-hidden mb-12">
            <img
              src={guide.image}
              alt={guide.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </div>

          {/* Content */}
          <div className="prose prose-lg prose-slate max-w-none
            prose-headings:font-semibold prose-headings:text-slate-900
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-slate-600 prose-p:leading-relaxed
            prose-li:text-slate-600
            prose-strong:text-slate-900
            prose-a:text-[#b91c1c] prose-a:no-underline hover:prose-a:underline
            prose-table:border-collapse prose-table:w-full
            prose-th:bg-slate-100 prose-th:p-3 prose-th:text-left prose-th:font-semibold prose-th:border prose-th:border-slate-200
            prose-td:p-3 prose-td:border prose-td:border-slate-200
          ">
            <ReactMarkdown components={markdownComponents}>{guide.content}</ReactMarkdown>
          </div>

          {/* CTA Box */}
          <div className="mt-12 bg-slate-900 rounded-2xl p-8 md:p-10 text-center">
            <h3 className="text-2xl md:text-3xl font-semibold text-white mb-3">
              Behöver du hjälp med din fasad?
            </h3>
            <p className="text-slate-300 mb-6">
              Vi erbjuder kostnadsfri besiktning och offert för träfasader.
            </p>
            <Link to="/#contact-form">
              <Button
                className="bg-[#b91c1c] hover:bg-[#991b1b] text-white h-12 px-8 rounded-full font-semibold shadow-lg"
                data-testid="article-cta-button"
              >
                Få kostnadsfri offert
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="bg-slate-50 py-16">
        <div className="container mx-auto max-w-4xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-8">
            Fler guider
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {otherGuides.map((otherGuide) => (
              <Link
                key={otherGuide.id}
                to={`/guider/${otherGuide.slug}`}
                className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={otherGuide.image}
                    alt={otherGuide.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-slate-900 group-hover:text-[#b91c1c] transition-colors line-clamp-2">
                    {otherGuide.title}
                  </h3>
                  <p className="text-sm text-slate-500 mt-2">{otherGuide.readTime} lästid</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
